import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseAudioPlaybackOptions {
  autoPlay?: boolean;
  onPlaybackEnd?: () => void;
  onPlaybackStart?: () => void;
  onError?: (error: Error) => void;
}

export interface UseAudioPlaybackReturn {
  play: (audioData: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  isPlaying: boolean;
  isPaused: boolean;
  currentTime: number;
  duration: number;
  error: string | null;
  setVolume: (volume: number) => void;
  volume: number;
}

export const useAudioPlayback = (
  options: UseAudioPlaybackOptions = {}
): UseAudioPlaybackReturn => {
  const {
    autoPlay = false,
    onPlaybackEnd,
    onPlaybackStart,
    onError,
  } = options;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(1.0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, []);

  const play = useCallback((audioData: string) => {
    // Stop any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    try {
      // Create new audio element with base64 data
      const audio = new Audio();
      
      // Handle base64 audio data
      if (audioData.startsWith('data:')) {
        audio.src = audioData;
      } else {
        // Assume it's base64 without the data URI prefix
        audio.src = `data:audio/mp3;base64,${audioData}`;
      }

      audio.volume = volume;

      // Set up event handlers
      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };

      audio.onplay = () => {
        setIsPlaying(true);
        setIsPaused(false);
        setError(null);
        onPlaybackStart?.();

        // Start updating current time
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
        }
        timeUpdateIntervalRef.current = setInterval(() => {
          setCurrentTime(audio.currentTime);
        }, 100);
      };

      audio.onpause = () => {
        setIsPaused(true);
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
        }
      };

      audio.onended = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentTime(0);
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
        }
        onPlaybackEnd?.();
      };

      audio.onerror = (_e) => {
        const errorMessage = 'Failed to play audio';
        setError(errorMessage);
        setIsPlaying(false);
        setIsPaused(false);
        onError?.(new Error(errorMessage));
      };

      // Store reference and play
      audioRef.current = audio;
      
      if (autoPlay) {
        audio.play().catch(_err => {
          setError('Auto-play failed. Click play to start.');
          setIsPlaying(false);
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize audio';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }, [volume, autoPlay, onPlaybackStart, onPlaybackEnd, onError]);

  const pause = useCallback(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const resume = useCallback(() => {
    if (audioRef.current && isPaused) {
      audioRef.current.play().catch(_err => {
        setError('Failed to resume playback');
      });
    }
  }, [isPaused]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    }
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  return {
    play,
    pause,
    resume,
    stop,
    isPlaying,
    isPaused,
    currentTime,
    duration,
    error,
    setVolume,
    volume,
  };
};